import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTheme } from '@material-ui/core/styles';
import { axiosErrorHandling } from '../../../../helper';
import { getParcelStatusBgColor } from '../../../../utils/parcelStatusColors';

type RiderParcelStatusSeriesRow = {
  date: string;
  counts: Record<string, number>;
};

type RiderParcelStatusSeriesResponse = {
  start: string;
  end: string;
  timezone: string;
  statuses: string[];
  series: RiderParcelStatusSeriesRow[];
  totals: Record<string, number>;
};

export default function ParcelStatusLineChart() {
  const theme = useTheme();
  const jwt = useSelector((state: any) => state?.auth?.jwt);

  const [start, setStart] = useState<string>(moment().subtract(13, 'days').format('YYYY-MM-DD'));
  const [end, setEnd] = useState<string>(moment().format('YYYY-MM-DD'));
  const [showChart, setShowChart] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RiderParcelStatusSeriesResponse | null>(null);

  useEffect(() => {
    if (!jwt || !showChart) {
      setLoading(false);
      setData(null);
      return;
    }

    let isMounted = true;
    setLoading(true);

    axios
      .get(`${process.env.REACT_APP_API_SERVER}/stats/rider/parcel-status-series`, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        params: {
          start,
          end,
        },
      })
      .then((res) => {
        if (!isMounted) return;
        setData(res.data);
      })
      .catch((error) => {
        axiosErrorHandling(error);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [jwt, showChart, start, end]);

  const chart = useMemo(() => {
    ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

    const series = data?.series ?? [];
    const statuses = data?.statuses ?? [];
    const labels = series.map((row) => moment(row.date).format('DD-MMM'));

    const datasets = statuses.map((status) => {
      const color = getParcelStatusBgColor(status) ?? theme.palette.grey[700];
      return {
        label: status,
        data: series.map((row) => Number(row.counts?.[status] ?? 0)),
        borderColor: color,
        backgroundColor: color,
        tension: 0.3,
      };
    });

    return {
      labels,
      datasets,
    };
  }, [data, theme.palette.grey]);

  const statuses = data?.statuses ?? [];

  return (
    <Paper className="w-full p-3 sm:p-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <Typography variant="h6">Parcel status by date</Typography>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <FormControlLabel
              control={<Switch color="primary" checked={showChart} onChange={(e) => setShowChart(e.target.checked)} />}
              label="Show chart"
            />

            <div className="flex flex-col xs:flex-row gap-3">
              <TextField
                label="Start"
                type="date"
                variant="standard"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={!showChart}
              />
              <TextField
                label="End"
                type="date"
                variant="standard"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={!showChart}
              />
            </div>
          </div>
        </div>

        {showChart && (
          <>
            {loading && <LinearProgress />}

            <Grid container>
              <Grid item xs={12}>
                <div className="w-full h-64 sm:h-72">
                  <Line
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top' as const,
                        },
                        title: {
                          display: false,
                          text: '',
                        },
                      },
                    }}
                    data={chart}
                  />
                </div>
              </Grid>
            </Grid>

            {statuses.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-sm text-gray-600">
                {statuses.map((status) => (
                  <div key={status} className="text-center">
                    <div className="font-medium text-gray-900">{Number(data?.totals?.[status] ?? 0)}</div>
                    <div className="truncate" title={status}>
                      {status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Paper>
  );
}
